import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { FiEdit, FiSave, FiX, FiCheckCircle, FiAlertCircle } from 'react-icons/fi';
import './ProfilePage.css';

const api = axios.create({
    baseURL: process.env.REACT_APP_BASE_URL,
});

api.interceptors.request.use((config) => {
    const token = localStorage.getItem('authToken');
    if (token) {
        config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
});

/* ─────────────────────────────────────────────────
   Fields that the PUT /api/user/update accepts.
   employeeId is excluded — it cannot be changed.
───────────────────────────────────────────────── */
const UPDATABLE_FIELDS = new Set([
    'username', 'password', 'email',
    'firstName', 'lastName', 'middleName',
    'dateOfBirth', 'gender',
    'phoneNumber', 'alternatePhone',
    'addressLine1', 'addressLine2', 'city', 'state', 'postalCode', 'country',
    'hireDate', 'jobTitle', 'department', 'managerId',
    'employmentType', 'workLocation',
    'salary',
    'bankAccountNumber', 'bankName', 'ifscCode',
    'emergencyContactName', 'emergencyContactPhone', 'emergencyContactRelationship',
    'isActive',
]);

/* Tabs */
const TABS = [
    { key: 'personal', label: 'Personal Information' },
    { key: 'address', label: 'Home Address' },
    { key: 'job', label: 'Job Information' },
    { key: 'salary', label: 'Salary Information' },
    { key: 'emergency', label: 'Emergency Contact' },
    { key: 'system', label: 'Account & System' },
];

const EDITABLE_TABS = new Set(['personal', 'address', 'job', 'salary', 'emergency']);

/* ── Build the diff payload: only fields that changed & are updatable ── */
const buildPayload = (original, updated) => {
    const payload = {};
    for (const key of UPDATABLE_FIELDS) {
        if (key === 'password') continue;          // handled separately
        if (updated[key] !== undefined && updated[key] !== original[key]) {
            payload[key] = updated[key] === '' ? null : updated[key];
        }
    }
    return payload;
};

/* ─────────────── Component ─────────────── */
const ProfilePage = () => {
    const [activeTab, setActiveTab] = useState('personal');
    const [loading, setLoading] = useState(true);
    const [pageError, setPageError] = useState('');
    const [profile, setProfile] = useState(null);
    const [formData, setFormData] = useState({});
    const [saving, setSaving] = useState(false);
    const [toast, setToast] = useState(null);   // { type: 'success'|'error', msg }
    const [editMode, setEditMode] = useState(
        Object.fromEntries(TABS.map(t => [t.key, false]))
    );

    /* ── fetch ── */
    useEffect(() => {
        const fetchProfile = async () => {
            try {
                const res = await api.get('/user/profile');
                setProfile(res.data);
                setFormData(res.data);
            } catch (err) {
                console.error(err);
                setPageError('Failed to load profile.');
            } finally {
                setLoading(false);
            }
        };
        fetchProfile();
    }, []);

    /* ── toast auto-dismiss ── */
    useEffect(() => {
        if (!toast) return;
        const t = setTimeout(() => setToast(null), 3500);
        return () => clearTimeout(t);
    }, [toast]);

    /* ── helpers ── */
    const getInitials = (p) =>
        `${p?.firstName?.charAt(0) ?? ''}${p?.lastName?.charAt(0) ?? ''}`.toUpperCase();

    const formatDate = (d) => {
        if (!d) return '—';
        return new Date(d).toLocaleDateString('en-IN', {
            timeZone: 'Asia/Kolkata', year: 'numeric', month: 'long', day: 'numeric',
        });
    };

    const formatDateTime = (dt) => {
        if (!dt) return '—';
        return new Date(dt).toLocaleString('en-IN', {
            timeZone: 'Asia/Kolkata', year: 'numeric', month: 'short', day: 'numeric',
            hour: '2-digit', minute: '2-digit',
        });
    };

    const formatSalary = (s) =>
        s != null ? `₹ ${Number(s).toLocaleString('en-IN')}` : '—';

    /* ── edit handlers ── */
    const handleEditToggle = (section) =>
        setEditMode(prev => ({ ...prev, [section]: !prev[section] }));

    const handleChange = useCallback((field, value) =>
        setFormData(prev => ({ ...prev, [field]: value })), []);

    const handleSave = async (section) => {
        setSaving(true);
        try {
            const payload = buildPayload(profile, formData);

            /* Also send new password only if user typed one */
            if (formData._newPassword) {
                payload.password = formData._newPassword;
            }

            if (Object.keys(payload).length === 0) {
                setToast({ type: 'info', msg: 'No changes to save.' });
                setEditMode(prev => ({ ...prev, [section]: false }));
                return;
            }

            await api.put('/user/update', payload);

            /* Merge changes into committed profile */
            const updated = { ...profile, ...payload };
            delete updated._newPassword;
            setProfile(updated);
            setFormData(updated);
            setEditMode(prev => ({ ...prev, [section]: false }));
            setToast({ type: 'success', msg: 'Profile updated successfully.' });
        } catch (err) {
            console.error('Save failed', err);
            const msg = err?.response?.data?.message || 'Failed to save. Please try again.';
            setToast({ type: 'error', msg });
        } finally {
            setSaving(false);
        }
    };

    const handleCancel = (section) => {
        setFormData({ ...profile });
        setEditMode(prev => ({ ...prev, [section]: false }));
    };

    /* ────────────── reusable input components ────────────── */

    /* Plain text input */
    const TextField = ({ label, fieldKey, section }) => (
        <div className="ep-info-item">
            <label>{label}</label>
            {editMode[section] ? (
                <input
                    type="text"
                    value={formData[fieldKey] ?? ''}
                    onChange={e => handleChange(fieldKey, e.target.value)}
                />
            ) : (
                <p>{profile[fieldKey] != null && profile[fieldKey] !== '' ? String(profile[fieldKey]) : '—'}</p>
            )}
        </div>
    );

    /* Date input */
    const DateField = ({ label, fieldKey, section }) => (
        <div className="ep-info-item">
            <label>{label}</label>
            {editMode[section] ? (
                <input
                    type="date"
                    value={formData[fieldKey] ? String(formData[fieldKey]).slice(0, 10) : ''}
                    onChange={e => handleChange(fieldKey, e.target.value)}
                />
            ) : (
                <p>{formatDate(profile[fieldKey])}</p>
            )}
        </div>
    );

    /* Number input */
    const NumberField = ({ label, fieldKey, section, prefix = '' }) => (
        <div className="ep-info-item">
            <label>{label}</label>
            {editMode[section] ? (
                <input
                    type="number"
                    value={formData[fieldKey] ?? ''}
                    onChange={e => handleChange(fieldKey, e.target.value === '' ? '' : Number(e.target.value))}
                />
            ) : (
                <p>{prefix}{profile[fieldKey] != null ? Number(profile[fieldKey]).toLocaleString('en-IN') : '—'}</p>
            )}
        </div>
    );

    /* Select / enum input */
    const SelectField = ({ label, fieldKey, section, options }) => (
        <div className="ep-info-item">
            <label>{label}</label>
            {editMode[section] ? (
                <select
                    value={formData[fieldKey] ?? ''}
                    onChange={e => handleChange(fieldKey, e.target.value)}
                >
                    <option value="">— Select —</option>
                    {options.map(o => (
                        <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                </select>
            ) : (
                <p>{profile[fieldKey] || '—'}</p>
            )}
        </div>
    );

    /* Password field (only visible while editing) */
    const PasswordField = ({ section }) => (
        editMode[section] ? (
            <div className="ep-info-item">
                <label>New Password <span className="ep-optional">(leave blank to keep current)</span></label>
                <input
                    type="password"
                    placeholder="Enter new password"
                    value={formData._newPassword ?? ''}
                    onChange={e => handleChange('_newPassword', e.target.value)}
                    autoComplete="new-password"
                />
            </div>
        ) : null
    );

    /* Read-only display */
    const ReadOnly = ({ label, value }) => (
        <div className="ep-info-item">
            <label>{label}</label>
            <p>{value != null && value !== '' ? String(value) : '—'}</p>
        </div>
    );

    /* Tab header row with Edit / Save / Cancel */
    const TabHeader = ({ title, section }) => (
        <div className="ep-tab-header">
            <h3>{title}</h3>
            {EDITABLE_TABS.has(section) && (
                !editMode[section] ? (
                    <button className="ep-edit-btn" onClick={() => handleEditToggle(section)}>
                        <FiEdit /> Edit
                    </button>
                ) : (
                    <div className="ep-edit-actions">
                        <button className="ep-save-btn" onClick={() => handleSave(section)} disabled={saving}>
                            <FiSave /> {saving ? 'Saving…' : 'Save'}
                        </button>
                        <button className="ep-cancel-btn" onClick={() => handleCancel(section)} disabled={saving}>
                            <FiX /> Cancel
                        </button>
                    </div>
                )
            )}
        </div>
    );

    /* ────────────── tab content ────────────── */
    const renderTabContent = () => {
        if (!profile) return null;

        switch (activeTab) {

            /* 1 ── Personal Information */
            case 'personal':
                return (
                    <div className="ep-tab-content">
                        <TabHeader title="Personal Information" section="personal" />
                        <div className="ep-info-grid">
                            <TextField label="First Name" fieldKey="firstName" section="personal" />
                            <TextField label="Middle Name" fieldKey="middleName" section="personal" />
                            <TextField label="Last Name" fieldKey="lastName" section="personal" />
                            <DateField label="Date of Birth" fieldKey="dateOfBirth" section="personal" />
                            <SelectField
                                label="Gender" fieldKey="gender" section="personal"
                                options={[
                                    { value: 'MALE', label: 'Male' },
                                    { value: 'FEMALE', label: 'Female' },
                                    { value: 'OTHER', label: 'Other' },
                                ]}
                            />
                            <TextField label="Phone Number" fieldKey="phoneNumber" section="personal" />
                            <TextField label="Alternate Phone" fieldKey="alternatePhone" section="personal" />
                            {/* <PasswordField section="personal" /> */}
                        </div>
                    </div>
                );

            /* 2 ── Home Address */
            case 'address':
                return (
                    <div className="ep-tab-content">
                        <TabHeader title="Home Address" section="address" />
                        <div className="ep-info-grid">
                            <TextField label="Address Line 1" fieldKey="addressLine1" section="address" />
                            <TextField label="Address Line 2" fieldKey="addressLine2" section="address" />
                            <TextField label="City" fieldKey="city" section="address" />
                            <TextField label="State" fieldKey="state" section="address" />
                            <TextField label="Postal Code" fieldKey="postalCode" section="address" />
                            <TextField label="Country" fieldKey="country" section="address" />
                        </div>
                    </div>
                );

            /* 3 ── Job Information */
            case 'job':
                return (
                    <div className="ep-tab-content">
                        <TabHeader title="Job Information" section="job" />
                        <div className="ep-info-grid">
                            <ReadOnly label="Employee ID" value={profile.employeeId} />
                            <TextField label="Job Title" fieldKey="jobTitle" section="job" />
                            <TextField label="Department" fieldKey="department" section="job" />
                            <SelectField
                                label="Employment Type" fieldKey="employmentType" section="job"
                                options={[
                                    { value: 'FULL_TIME', label: 'Full Time' },
                                    { value: 'PART_TIME', label: 'Part Time' },
                                    { value: 'CONTRACT', label: 'Contract' },
                                    { value: 'INTERN', label: 'Intern' },
                                ]}
                            />
                            <TextField label="Work Location" fieldKey="workLocation" section="job" />
                            <NumberField label="Manager ID" fieldKey="managerId" section="job" />
                            <DateField label="Hire Date" fieldKey="hireDate" section="job" />
                            <ReadOnly label="Status" value={profile.isActive ? 'Active' : 'Inactive'} />
                            <ReadOnly label="Role" value={profile.role} />
                        </div>
                    </div>
                );

            /* 4 ── Salary Information */
            case 'salary':
                return (
                    <div className="ep-tab-content">
                        <TabHeader title="Salary Information" section="salary" />
                        <div className="ep-info-grid">
                            <NumberField label="Salary (₹)" fieldKey="salary" section="salary" prefix="₹ " />
                            <TextField label="Bank Name" fieldKey="bankName" section="salary" />
                            <TextField label="Account Number" fieldKey="bankAccountNumber" section="salary" />
                            <TextField label="IFSC Code" fieldKey="ifscCode" section="salary" />
                        </div>
                    </div>
                );

            /* 5 ── Emergency Contact */
            case 'emergency':
                return (
                    <div className="ep-tab-content">
                        <TabHeader title="Emergency Contact" section="emergency" />
                        <div className="ep-info-grid">
                            <TextField label="Contact Name" fieldKey="emergencyContactName" section="emergency" />
                            <TextField label="Contact Phone" fieldKey="emergencyContactPhone" section="emergency" />
                            <TextField label="Relationship" fieldKey="emergencyContactRelationship" section="emergency" />
                        </div>
                    </div>
                );

            /* 6 ── Account & System (all read-only) */
            case 'system':
                return (
                    <div className="ep-tab-content">
                        <div className="ep-tab-header">
                            <h3>Account &amp; System Info</h3>
                        </div>
                        <div className="ep-info-grid">
                            <ReadOnly label="User ID" value={profile.id} />
                            <ReadOnly label="Username" value={profile.username} />
                            <ReadOnly label="Email" value={profile.email} />
                            <ReadOnly label="Employee ID" value={profile.employeeId} />
                            <ReadOnly label="Role" value={profile.role} />
                            <ReadOnly label="Account Created" value={formatDateTime(profile.createdAt)} />
                            <ReadOnly label="Last Updated" value={formatDateTime(profile.updatedAt)} />
                            <ReadOnly label="Last Login" value={formatDateTime(profile.lastLogin)} />
                        </div>
                    </div>
                );

            default:
                return null;
        }
    };

    /* ── loading / error states ── */
    if (loading) {
        return (
            <div className="hr-content animate-fade-in">
                <div className="ep-state">
                    <div className="ep-spinner" />
                    <p>Loading profile…</p>
                </div>
            </div>
        );
    }

    if (pageError) {
        return (
            <div className="hr-content animate-fade-in">
                <div className="ep-state ep-state--error">
                    <p>{pageError}</p>
                </div>
            </div>
        );
    }

    /* ── main render ── */
    return (
        <div className="hr-content animate-fade-in">
            <div className="ep-wrapper">

                {/* ── Toast notification ── */}
                {toast && (
                    <div className={`ep-toast ep-toast--${toast.type}`}>
                        {toast.type === 'success' && <FiCheckCircle />}
                        {toast.type === 'error' && <FiAlertCircle />}
                        {toast.type === 'info' && <FiCheckCircle />}
                        <span>{toast.msg}</span>
                        <button className="ep-toast-close" onClick={() => setToast(null)}><FiX /></button>
                    </div>
                )}

                <h2 className="ep-page-title">Admin Profile</h2>

                <div className="ep-profile-container">

                    {/* ── Left panel ── */}
                    <div className="ep-profile-left">
                        <div className="ep-photo-wrap">
                            <div className="ep-photo-placeholder">
                                {getInitials(profile)}
                            </div>
                        </div>

                        <div className="ep-basic-info">
                            <h3>{profile.firstName} {profile.lastName}</h3>
                            <p className="ep-position">{profile.jobTitle || '—'}</p>

                            <div className="ep-info-item">
                                <label>Email</label>
                                <p>{profile.email}</p>
                            </div>
                            {/* <div className="ep-info-item">
                                <label>Phone</label>
                                <p>{profile.phoneNumber || '—'}</p>
                            </div> */}
                            <div className="ep-info-item">
                                <label>Username</label>
                                <p>{profile.username}</p>
                            </div>
                            <div className="ep-info-item">
                                <label>Department</label>
                                <p>{profile.department || '—'}</p>
                            </div>
                            <div className="ep-info-item">
                                <label>Status</label>
                                <p className={`ep-status-badge ${profile.isActive ? 'ep-status--active' : 'ep-status--inactive'}`}>
                                    {profile.isActive ? 'Active' : 'Inactive'}
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* ── Right panel ── */}
                    <div className="ep-profile-right">
                        <div className="ep-tabs">
                            {TABS.map(({ key, label }) => (
                                <button
                                    key={key}
                                    className={`ep-tab-button${activeTab === key ? ' active' : ''}`}
                                    onClick={() => setActiveTab(key)}
                                >
                                    {label}
                                </button>
                            ))}
                        </div>

                        <div className="ep-tab-content-container">
                            {renderTabContent()}
                        </div>
                    </div>

                </div>
            </div>
        </div>
    );
};

export default ProfilePage;

